import React, { useState, useRef } from 'react';
import DateRangeSelector from '../DateRangeSelector';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { useSearchParams } from 'react-router-dom';

const GIPlanningSelectionHeader = ({
  startDate,
  endDate,
  clients,
  companies,
  selectedClient,
  selectedCompany,
  selectedEntity,
  entityOptions,
  includeAllEntities,
  planningData,
  onDatesChange,
  onClientChange,
  onCompanyChange,
  onEntityChange,
  onIncludeAllEntitiesChange
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const selectStyles = useSelectStyles();
  const [searchParams] = useSearchParams();
  const jailRef = useRef(searchParams.has('jail'));
  const jail = jailRef.current;

  // Companies contient maintenant seulement les parents (IsGroup=true)
  const filteredCompanies = companies;

  const filteredClients = (() => {
    if (!selectedCompany)
      return clients;

    return planningData.clients.map(({ ClientId, Name }) => ({
      value: ClientId,
      label: Name
    }));
  })();

  // Fonction pour afficher un résumé des sélections
  const getSelectionSummary = () => {
    const parts = [];

    if (selectedClient) {
      parts.push(`Client: ${selectedClient.label}`);
    }

    if (selectedCompany) {
      let companyText = `Company: ${selectedCompany.label}`;
      if (selectedEntity) {
        companyText += ` > Entity: ${selectedEntity.label}`;
      } else if (includeAllEntities && entityOptions.length > 0) {
        companyText += ` (+ ${entityOptions.length} entités)`;
      }
      parts.push(companyText);
    }

    if (startDate && endDate) {
      const start = startDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      const end = endDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      parts.push(`${start} - ${end}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'Aucune sélection';
  };

  return (
    <div className="py-4 mb-6 relative z-50">
      {/* Toggle header pour mobile/tablet - masqué en desktop */}
      <div
        className="flex items-center justify-between cursor-pointer md:hidden relative"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Titre centré - affiché uniquement quand le composant est déplié */}
        {isExpanded && (
          <div className="absolute inset-x-0 text-center">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Sélection</div>
          </div>
        )}

        {/* Résumé des sélections - affiché uniquement quand replié */}
        <div className={`flex-1 ${isExpanded ? 'invisible' : 'visible'} max-w-[calc(100%-2rem)]`}>
          {!isExpanded && (
            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 text-left">
              {getSelectionSummary()}
            </div>
          )}
        </div>

        {/* Chevron (à droite) */}
        <div className="ml-3 z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Contenu du filtre - toujours visible en desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block md:pt-0 pt-4`}>
        <div className={`grid grid-cols-1 md:grid-cols-${jail ? '1' : '4'} gap-3`}>
          {!jail && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                  Groupe ({selectedCompany ? 1 : filteredCompanies.length})
                </label>
                <Select
                  value={selectedCompany}
                  onChange={onCompanyChange}
                  options={filteredCompanies}
                  placeholder="Sélectionner un groupe..."
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className="text-sm"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => "Aucune company trouvée"}
                  menuPortalTarget={document.body}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                  Entité ({selectedEntity ? 1 : entityOptions.length})
                </label>
                <div className="space-y-2">
                  <Select
                    value={selectedEntity}
                    onChange={onEntityChange}
                    options={entityOptions}
                    placeholder="Sélectionner une entité..."
                    isClearable
                    isSearchable
                    isDisabled={!selectedCompany || entityOptions.length === 0}
                    styles={selectStyles}
                    className="text-sm"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => "Aucune entité trouvée"}
                    menuPortalTarget={document.body}
                  />
                  {selectedCompany && entityOptions.length > 0 && (
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={includeAllEntities}
                        onChange={(e) => onIncludeAllEntitiesChange(e.target.checked)}
                        disabled={!!selectedEntity}
                        className="text-blue-600"
                      />
                      <span className={selectedEntity ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}>
                        Inclure toutes les entités
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                  Client ({selectedClient ? 1 : filteredClients.length})
                </label>
                <Select
                  value={selectedClient}
                  onChange={onClientChange}
                  options={filteredClients}
                  placeholder="Sélectionner un client..."
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  className="text-sm"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => "Aucun client trouvé"}
                  menuPortalTarget={document.body}
                />
              </div>
            </>
          )}

          {/* Sélection de dates */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Période de visualisation
            </label>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onDatesChange={onDatesChange}
              maxDateRange={{ value: 6, unit: 'months' }}
              includeFuture={true}
              relative={false}
            />
          </div>
        </div>

        {/* Informations supplémentaires */}
        {(selectedClient || selectedCompany) && startDate && endDate && (
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4 flex-wrap">
              {selectedClient && (
                <span>Client: <span className="font-medium">{selectedClient.label}</span></span>
              )}
              {selectedCompany && (
                <span>Groupe: <span className="font-medium">{selectedCompany.label}</span></span>
              )}
              {selectedEntity && (
                <span>Entité: <span className="font-medium">{selectedEntity.label}</span></span>
              )}
              {selectedClient && selectedCompany && (
                <span className="text-blue-600 dark:text-blue-400 font-medium">Filtrage combiné</span>
              )}
              <span>
                Période: <span className="font-medium">
                  {startDate.toLocaleDateString('fr-FR')} - {endDate.toLocaleDateString('fr-FR')}
                </span>
              </span>
              <span>
                Durée: <span className="font-medium">
                  {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} jours
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GIPlanningSelectionHeader;